import * as React from "react";
import * as ReactDOM from "react-dom";
import * as moment from "moment";
import * as globals from "../../../globals";
import * as apiSettings from "../../../api/settings";
import * as apiAppInfo from "../../../api/appInfo";
import * as utils from "../../../utils/utils";
import { ISetting, IStatus, ITemplate, IUser, IServerSettings, ILicence } from "../../../models/get";
import { Loader, LoaderStyle } from "../../../components/loader";
import SettingsNav from "../nav";
import { Button, ButtonSize, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { Alert, AlertType } from "../../../components/alert";
import { GroupBox } from "../../../components/groupBox";
import { InputMask, InputSize } from "../../../components/input";
import Dialog from "./dialog";
import { versionCompare } from "../../../utils/utils";

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt an, ob der Dialog geöffnet ist.
     */
    isDialogOpen?: boolean,

    /**
     * Enthält den momentan angemeldeten Benutzer.
     */
    currentUser?: IUser,

    /**
     * Die Version des Terminplaners.
     */
    version?: string,

    /**
     * Die Version von calendar.in-com.de
     */
    versionServer?: string,

    /**
     * Enthält das Ergebnis des Vergleichs der beiden Versionsnummern.*/
    versionCompareResult?: number,

    /**
     * Enthält die registrierte Lizenz des Kunden.
     */
    licence?: ILicence,

    /**
     * Angabe ob alle Lizenzen aufgebraucht sind.
     */
    numberOfLeftLicences?: number,

    linkToUpdateDocs?: string,

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export class Help extends React.Component<{}, IState>
{
    private _serverSettings: IServerSettings = {};

    /**
     * Erstellt eine neue Instanz der Klasse @see Help.
     * @param props Alle gesetzten Eigenschaften.
     */
    constructor(props)
    {
        super(props);

        this.state = {
            isLoading: true,
            errors: {}
        }

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props)
    {
        this._serverSettings = await globals.getServerSettingsAsync();
        var currentUser: IUser = await globals.getCurrentUserAsync();
        var licence: ILicence = await apiSettings.getLicence();
        var version: string = await apiAppInfo.getVersion();
        var release: string = await apiAppInfo.getRelease();
        var serverVersion: string = null;
        var versionCompareResult: number = null;

        try
        {
            serverVersion = await apiAppInfo.getServerVersion();
        }
        catch (e)
        {
            console.error(e);
        }
        finally
        {
            if (serverVersion != null)
                versionCompareResult = utils.versionCompare(version, serverVersion);
        }

        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.currentUser = currentUser;
            state.version = version;
            state.versionServer = serverVersion;
            state.versionCompareResult = versionCompareResult;
            state.licence = licence;

            let month: number = parseInt(release.getLeftChars(release.length === 3 ? 1 : 2));
            let year: number = parseInt("20" + release.getRightChars(release.length === 3 ? 1 : 2));
            if (month && year)
            {
                state.linkToUpdateDocs = `http://documentation.in-com.de/display/WWTP/${moment.months()[month - 1]}+${year}`;
            }

            return state;
        });
    }

    /**
     * Aktualisiert die Liste.
     */
    private async refreshAsync()
    {
        this.setState({
            isLoading: true
        });

        await this.initAsync(this.props);
    }

    /**
     * Öffnet den Dialog zum ändern des Lizenzschlüssel.
     */
    private onChangeLicenceKey = () =>
    {
        this.setState({
            isDialogOpen: true
        });
    }

    /**
     * Wird aufgerufen, sobald sich der Dialog schließt.
     * @param refresh Angabe, ob die Ansicht aktualisiert werden soll.
     */
    private onDialogClosed = async (refresh: boolean) =>
    {
        if (refresh)
        {
            await this.refreshAsync();
            this.setState({
                isDialogOpen: false
            });
        }
        else
        {
            this.setState({
                isDialogOpen: false
            });
        }
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        var {
            isLoading,
            isDialogOpen,
            currentUser,
            version,
            versionServer,
            versionCompareResult,
            licence,
            linkToUpdateDocs,
            errors,
            alertMessage,
            alertType
        } = this.state;

        if (isLoading)
        {
            return (
                <Loader
                    style={{ marginTop: 40 }}
                    loaderStyle={LoaderStyle.style1} />
            );
        }

        const isSuperAdmin: boolean = currentUser.is_super_admin;
        const isAdmin: boolean = currentUser.is_admin;
        const isSass: boolean = this._serverSettings.Mode === "saas";

        return (
            <div className="settings-content">
                <div className="settings-content_container">
                    <div className="settings-content_nav">
                        <SettingsNav selected="help" />
                    </div>
                    <div className="settings-content_alert">
                        <Alert
                            alertType={alertType}
                            isClosable={true}
                            isVisible={alertMessage != null}
                            onHide={() => this.setState({ alertMessage: null })}>
                            {alertMessage}
                        </Alert>
                    </div>
                    <div className="settings-help">
                        <div className="settings-help_content-wrapper">
                            <ScrollViewer
                                HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                                VerticalScrollBarVisibility={ScrollBarVisibility.auto}>
                                <div className="settings-help_content">
                                    <GroupBox
                                        headerText="Über Terminplaner"
                                        headerMargin={{
                                            bottom: 20
                                        }}>
                                        <div className="settings-help_content_product">
                                            <img
                                                className="settings-help_content_product_logo"
                                                src="/img/logos/terminplaner_logo.png" />
                                            <span className="settings-help_content_product_name">in-com Terminplaner</span>
                                        </div>
                                        <div className="settings-help_content_separator"></div>
                                        {
                                            versionCompareResult === 0 &&
                                            <div className="settings-help_content_version">
                                                <div className="settings-help_content_version_icon">
                                                    <i className="mdi mdi-checkbox-marked-circle"></i>
                                                </div>
                                                <div className="settings-help_content_version_text">
                                                    <span>Der Terminplaner ist auf dem neuesten Stand.</span>
                                                    <span>Version {version} (Offizieller Build)</span>
                                                </div>
                                            </div>
                                        }
                                        {
                                            versionCompareResult > 0 &&
                                            <div className="settings-help_content_version">
                                                <div className="settings-help_content_version_icon">
                                                    <i className="mdi mdi-checkbox-marked-circle"></i>
                                                </div>
                                                <div className="settings-help_content_version_text">
                                                    <span>Der Terminplaner ist auf dem neuesten Stand.</span>
                                                    <span>Version {version} (Inoffizieller Build)</span>
                                                </div>
                                            </div>
                                        }
                                        {
                                            (!isSass && versionCompareResult < 0) &&
                                            <div className="settings-help_content_version">
                                                <div className="settings-help_content_version_icon alert">
                                                    <i className="mdi mdi-alert-circle"></i>
                                                </div>
                                                <div className="settings-help_content_version_text">
                                                    <span>Eine neue Version des Terminplaners steht zur Verfügung.</span>
                                                    <span>Installierte Version {version} -> Neue Version {versionServer}</span>
                                                </div>
                                            </div>
                                        }
                                        {
                                            (isSass && versionCompareResult < 0) &&
                                            <div className="settings-help_content_version">
                                                <div className="settings-help_content_version_icon info">
                                                    <i className="mdi mdi-information"></i>
                                                </div>
                                                <div className="settings-help_content_version_text">
                                                    <span>Eine neue Version wird in kürze installiert.</span>
                                                    <span>Installierte Version {version} -> Neue Version {versionServer}</span>
                                                </div>
                                            </div>
                                        }
                                        {
                                            (versionCompareResult == null || isNaN(versionCompareResult)) &&
                                            <div className="settings-help_content_version">
                                                <div className="settings-help_content_version_icon error">
                                                    <i className="mdi mdi-alert"></i>
                                                </div>
                                                <div className="settings-help_content_version_text">
                                                    <span>Es ist ein Fehler beim prüfen der Version aufgetreten.</span>
                                                    <span>Version {version}</span>
                                                </div>
                                            </div>
                                        }
                                        <div className="settings-help_content_separator"></div>
                                        <div className="settings-help_content_actions">
                                            {/*
                                                (isAdmin && !isSass && versionCompareResult < 0) &&
                                                <Button
                                                    type={ButtonType.primary}
                                                    size={ButtonSize.normal}
                                                    text="Update installieren"></Button>
                                            */}
                                            <a
                                                style={{
                                                    marginBottom: 10
                                                }}
                                                href={linkToUpdateDocs}
                                                target="_blank">Anmerkungen zu dieser Version</a>
                                            <a
                                                style={{
                                                    marginBottom: 10
                                                }}
                                                href="http://documentation.in-com.de/display/WWTP/Bestattungskalender"
                                                target="_blank">Hilfe des Terminplaners aufrufen</a>
                                            <a
                                                href="https://in-com.de/Kontakt"
                                                target="_blank">Kontaktinformationen</a>
                                        </div>
                                    </GroupBox>
                                    <GroupBox
                                        headerText="Lizenz"
                                        headerMargin={{
                                            bottom: 20
                                        }}>
                                        <div className="settings-help_content_licence">
                                            <span>Registriert für: {licence.customerName} ({licence.customerNumber})</span>
                                            <span>Lizenzen: {licence.numberOfLicences === 0 ? "unbegrenzt" : licence.numberOfLicences}</span>
                                        </div>
                                        {
                                            (isSuperAdmin || isAdmin) &&
                                            <div className="settings-help_content_separator"></div>
                                        }
                                        {
                                            (isSuperAdmin || isAdmin) &&
                                            <Button
                                                type={ButtonType.primary}
                                                size={ButtonSize.normal}
                                                text="Lizenzschlüssel ändern"
                                                onClick={this.onChangeLicenceKey}></Button>
                                        }
                                    </GroupBox>
                                </div>
                            </ScrollViewer>
                        </div>
                    </div>
                    {
                        isDialogOpen &&
                        <Dialog onClose={this.onDialogClosed} />
                    }
                </div>
            </div>
        );
    }
}